/**
 * One campaign's line items, in the Line items panel: the list X answers, a create that
 * is locked the moment an answer says the object may exist, and — since PR 3 of
 * audits/2026-10-29/gui/plan.md — one line item's own screen, opened by Open on its row:
 * its header with Start and Pause, its targeting, the forms that add a criterion, and its
 * ads, as panels side by side instead of sections under the table.
 *
 * 🔴 **A line item is the object that spends.** The campaign above it holds the
 * budget and cannot deliver on its own, so every guard the campaign create has is
 * here — Create locked after `exists` or `unknown`, lifted only by the read that
 * does not lag, a deliberate way past it that names what it may cost, and no
 * corrective write of any kind. The decisions are in `lib/line-items.ts` and the
 * words in `lib/line-item-copy.ts`; this file renders them and decides nothing.
 *
 * It is a part of the Campaigns screen rather than a page because a line item has no
 * meaning without its campaign: the campaign is what carries the budget, the funding
 * instrument and the only delivery signal X reports.
 */
import { type FormEvent, useEffect, useRef, useState } from "react";
import {
	type Campaign,
	type LineItem,
	type LineItemObjective,
	type StatsAnswer,
	UnauthorizedError,
	type XAccount,
	api,
} from "../lib/api.ts";
import { FIGURE_LABELS, figureCellText } from "../lib/campaign-card-copy.ts";
import { appendRows, foundById, mayCreateAgain, outcomeAfterReload } from "../lib/campaigns.ts";
import { holdInFlight, inFlight, lineItemLockKey, useHeldOutcome } from "../lib/create-locks.ts";
import {
	CREATE_ANYWAY,
	CREATE_HEADING,
	CREATE_LOCKED,
	CREATE_LOCK_IS_LOCAL,
	EXISTS,
	FIXED_SHAPE,
	FLIGHT_IS_YOURS,
	MORE_BANNER,
	NO_SERVABLE,
	NO_TARGETING,
	OBJECTIVES,
	OBJECTIVE_NOTE,
	PRICE_IS_X,
	PRICE_IS_X_TITLE,
	READ_ONLY,
	UNKNOWN,
	bidText,
	creativeHint,
	flightText,
	loadedLabel,
	outcomeTitle,
} from "../lib/line-item-copy.ts";
import { BACK_TO_LINE_ITEMS, OPEN_LINE_ITEM } from "../lib/line-item-screen-copy.ts";
import {
	type LineItemOutcome,
	cannotDeliverYet,
	lineItemCreateOutcome,
} from "../lib/line-items.ts";
import { SPEND_LABEL, billedCellText } from "../lib/stats-copy.ts";
import { billedRowState, idsForStats, metricRowState, sevenDayWindow } from "../lib/stats.ts";
import { Badge, Button, InfoBanner, Input, Select, Table } from "../ui/index.ts";
import { Creative } from "./Creative.tsx";
import { Targeting } from "./Targeting.tsx";

interface Props {
	campaign: Campaign;
	account: XAccount;
	/** `canWrite` on the account, as the campaigns screen resolved it. */
	writable: boolean;
	/**
	 * The code to label a billed figure with, as the campaigns screen resolved it from
	 * the account's funding instruments.
	 *
	 * 🔴 A **prop and not a second read**: X's analytics answer carries no currency, the
	 * campaigns screen already holds the instruments, and a read of its own here would
	 * ask X the same question twice to put the same code on the same figure. `null` is a
	 * real answer — the instruments disagreed, or there are none — and means the raw
	 * micro figure is shown rather than a guessed code.
	 */
	currency: string | null;
	/**
	 * Told whether a line item's screen is open, so the page can give it the lower half's
	 * width; told `false` when this section goes. Optional: the section works without it.
	 */
	onScreen?: (open: boolean) => void;
	/**
	 * Changed by the page when it opens something the screen would hide — the card: the
	 * screen closes back to the list. A count, not a flag, so two openings in a row are two.
	 */
	closeScreen?: number;
}

export function LineItems({ campaign, account, writable, currency, onScreen, closeScreen }: Props) {
	const [rows, setRows] = useState<LineItem[]>([]);
	/*
	 * The seven-day window, built once per mount — see the campaigns screen: a window
	 * rebuilt on every render would change mid-hour and make two figures on one screen
	 * answer two different questions.
	 */
	const [billedWindow] = useState(() => sevenDayWindow(new Date()));
	const [billed, setBilled] = useState<StatsAnswer | null>(null);
	const [billedFailed, setBilledFailed] = useState<string | null>(null);
	/**
	 * The line item whose screen is open, held as the row itself — one at a time, for the
	 * reason the campaigns screen holds one: a narrowing aimed at the wrong line item leaves
	 * the right one reaching everyone. Until PR 3 the targeting and the creative were two
	 * openings, each its own row; one screen holds both now, so they cannot name two rows.
	 */
	const [openLineItem, setOpenLineItem] = useState<LineItem | null>(null);
	const screenOpen = openLineItem !== null;
	// biome-ignore lint/correctness/useExhaustiveDependencies: whether the screen is open IS the subject; the page's callback is the page's
	useEffect(() => {
		onScreen?.(screenOpen);
	}, [screenOpen]);
	// biome-ignore lint/correctness/useExhaustiveDependencies: at unmount only — a section that is gone has no screen open
	useEffect(() => () => onScreen?.(false), []);
	// The page's count is the event; the first render closes nothing that is open.
	useEffect(() => {
		if (closeScreen !== undefined) setOpenLineItem(null);
	}, [closeScreen]);
	const [cursor, setCursor] = useState<string | null>(null);
	const [loading, setLoading] = useState(true);
	const [failed, setFailed] = useState<string | null>(null);
	/**
	 * The last create's answer, and with it the lock on Create — held in `create-locks.ts`
	 * under this account and this campaign, so it outlives this section: the New campaign
	 * toggle, Hide line items, another account and another campaign all unmount it, and
	 * each used to clear the lock (the open defect of production since #234). A reload
	 * still clears it, as CREATE_LOCK_IS_LOCAL says.
	 */
	const lockKey = lineItemLockKey(account.xAccountId, campaign.id);
	const [outcome, setOutcome] = useHeldOutcome<LineItemOutcome>(lockKey);
	const [busy, setBusy] = useState(false);
	const [formError, setFormError] = useState<string | null>(null);
	const [start, setStart] = useState("");
	const [end, setEnd] = useState("");
	const [objective, setObjective] = useState<LineItemObjective>("ENGAGEMENTS");
	/** Which campaign the rows on screen belong to, checked after every await. */
	const chosen = useRef(campaign.id);

	/**
	 * The first page again, for this campaign.
	 *
	 * Out here rather than only inside the effect because a stop changes what a row
	 * says: the status in the table comes from this read, and a screen that kept
	 * showing ACTIVE after X had answered PAUSED would be lying about the one thing
	 * the operator just did.
	 */
	const readFirstPage = () => {
		chosen.current = campaign.id;
		setLoading(true);
		setFailed(null);
		api
			.lineItems(account.xAccountId, campaign.id)
			.then((page) => {
				if (chosen.current !== campaign.id) return;
				setRows(page.lineItems);
				/*
				 * 🔴 The open screen is re-pointed at the refreshed row, not left holding the
				 * object it was opened with. The Ads panel checks the line item's status before
				 * it attaches, and the header shows it beside Start and Pause: a screen still
				 * showing PAUSED after X answered ACTIVE would be the one lie that matters there.
				 */
				setOpenLineItem((open) =>
					open ? (page.lineItems.find((r) => r.id === open.id) ?? open) : null,
				);
				setCursor(page.page.nextCursor);
				setLoading(false);
			})
			.catch((e) => {
				if (chosen.current !== campaign.id || e instanceof UnauthorizedError) return;
				setFailed(e instanceof Error ? e.message : String(e));
				setLoading(false);
			});
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: the two ids ARE the subject — this effect must re-run when the campaign or the account changes, and `readFirstPage` closes over exactly those two
	useEffect(() => {
		// 🔴 The rows are emptied, not merged: another campaign's line items have
		// different ids and so are not duplicates of anything `appendRows` can drop.
		// Page two of one campaign under page one of another is a screen that lies about
		// whose spend an operator is looking at.
		setRows([]);
		setOpenLineItem(null);
		setCursor(null);
		// 🔴 The create's answer is NOT cleared here: it is held per campaign, so another
		// campaign reads its own, and coming back to this one reads this one's lock again.
		readFirstPage();
	}, [campaign.id, account.xAccountId]);

	async function loadMore() {
		const at = cursor;
		const asked = campaign.id;
		if (at === null || busy) return;
		setBusy(true);
		try {
			const next = await api.lineItems(account.xAccountId, asked, at);
			if (chosen.current !== asked) return;
			setRows((held) => appendRows(held, next.lineItems));
			setCursor(next.page.nextCursor);
		} catch (e) {
			if (e instanceof UnauthorizedError) return;
			// The cursor is kept, so the same button retries the same page.
			setFailed(e instanceof Error ? e.message : String(e));
		} finally {
			setBusy(false);
		}
	}

	/**
	 * The one read that can lift the lock, and only where there is an id to read
	 * with. `unknown` carries none — nothing here can lift that, and the way out is
	 * the deliberate action, not a list that came back 200.
	 */
	async function checkById() {
		if (outcome?.kind !== "exists" || outcome.id === null) return;
		setBusy(true);
		try {
			const { status, body } = await api.lineItem(outcome.id, account.xAccountId);
			setOutcome(outcomeAfterReload(outcome, foundById(status, body, outcome.id)));
		} catch (e) {
			if (e instanceof UnauthorizedError) return;
			// Nothing established: the lock stays exactly where it was.
			setFailed(e instanceof Error ? e.message : String(e));
		} finally {
			setBusy(false);
		}
	}

	async function create(e: FormEvent) {
		e.preventDefault();
		// 🔴 The lock is the handler's own rule; the button's `disabled` only mirrors it.
		if (!writable || busy || locked) return;
		if (!start.trim() || !end.trim()) {
			setFormError("Start and end are both required.");
			return;
		}
		setFormError(null);
		setBusy(true);
		holdInFlight(lockKey, true);
		try {
			const { status, body } = await api.createLineItem({
				account: account.xAccountId,
				campaignId: campaign.id,
				startTime: start,
				endTime: end,
				objective,
			});
			const answered = lineItemCreateOutcome(status, body);
			setOutcome(answered);
			if (answered.kind === "created") {
				setStart("");
				setEnd("");
			}
		} catch (err) {
			if (err instanceof UnauthorizedError) return;
			// The request may have reached X: an answer that never arrived is `unknown`,
			// never a refusal, for the same reason the worker reads a 5xx that way.
			setOutcome({
				kind: "unknown",
				error: err instanceof Error ? err.message : String(err),
			});
		} finally {
			setBusy(false);
			holdInFlight(lockKey, false);
		}
	}

	// 🔴 A create still out holds the lock too: this section may be a new mount of one
	// whose request has not answered yet (lib/create-locks.ts, `holdInFlight`).
	const locked = inFlight(lockKey) || (outcome !== null && !mayCreateAgain(outcome));

	/*
	 * What these line items spent in the last seven days.
	 *
	 * 🔴 Cleared before every read, for the reason the campaigns screen states: the rows
	 * under this figure change — another campaign, another page — and a figure left
	 * standing would sit against rows it was never about.
	 */
	const picked = idsForStats(rows.map((r) => r.id));
	const askedFor = picked.ids.join(",");
	// biome-ignore lint/correctness/useExhaustiveDependencies: the account and the ids on screen ARE the subject; the window is built once on purpose
	useEffect(() => {
		setBilled(null);
		setBilledFailed(null);
		if (askedFor === "") return;
		api
			.stats(account.xAccountId, "LINE_ITEM", askedFor.split(","), billedWindow)
			.then(setBilled)
			.catch((e: unknown) => {
				if (e instanceof UnauthorizedError) return;
				setBilledFailed(e instanceof Error ? e.message : String(e));
			});
	}, [account.xAccountId, askedFor]);

	return (
		<div className="flex min-h-0 flex-col gap-2">
			<h2 className="text-sm font-semibold text-content">
				Line items — {campaign.name}
				<span className="ml-2 font-mono text-xs text-muted">{campaign.id}</span>
			</h2>

			{openLineItem ? (
				/*
				 * 🔴 The line item's own screen. The list is put away while it is open rather
				 * than kept above it: the screen is the panel's height, and the list would push
				 * Start and Pause back under a scroll — the defect this screen was cut to remove.
				 * `@container` because the panel this sits in is half the page, or the whole of
				 * it, by the page's choice; the three columns come where they fit.
				 */
				<div className="@container flex flex-col gap-2">
					<div>
						<Button type="button" variant="ghost" size="sm" onClick={() => setOpenLineItem(null)}>
							← {BACK_TO_LINE_ITEMS}
						</Button>
					</div>
					<div className="grid gap-3 @3xl:grid-cols-3">
						<Targeting
							/*
							 * 🔴 Keyed on the line item, so opening this panel on another row builds a
							 * NEW one instead of re-pointing this one. Without it the panel keeps every
							 * piece of state the reset effect inside it does not name — and that effect
							 * names five fields, not all of them. The one that cost money was the
							 * unconfirmed start warning: shown for line item A, still held when the
							 * panel moved to B, so one press on B sent ACTIVE with no warning (d6,
							 * 2026-10-08). `searchFailed` has the same shape and is cleared nowhere.
							 *
							 * A sixth line in that effect would have closed the one field; the key
							 * closes the class, which is why it is here and the effect is left as it
							 * is rather than growing a list that the next field will not join.
							 */
							key={openLineItem.id}
							lineItem={openLineItem}
							campaign={campaign}
							account={account}
							writable={writable}
							// A switch changes the status this screen shows, so the page is read again.
							onSwitched={readFirstPage}
						/>
						{/* Keyed for the same class: `chosen` is what Promote sends, and it must not cross rows. */}
						<Creative
							key={openLineItem.id}
							lineItem={openLineItem}
							account={account}
							onPromoted={readFirstPage}
						/>
					</div>
				</div>
			) : (
				<>
					<p className="text-sm text-muted">{NO_SERVABLE}</p>

					{failed && (
						<InfoBanner variant="danger" title="X did not answer about this campaign's line items.">
							{failed}
						</InfoBanner>
					)}
					{cursor !== null && (
						<InfoBanner variant="warning" title={MORE_BANNER.title}>
							{MORE_BANNER.body}
						</InfoBanner>
					)}

					<Table<LineItem>
						columns={[
							{
								id: "id",
								header: "Line item",
								accessor: (r) => <div className="font-mono text-xs text-content">{r.id}</div>,
							},
							{
								id: "status",
								header: "Status",
								accessor: (r) => (
									<Badge variant={r.entityStatus === "ACTIVE" ? "info" : "default"}>
										{r.entityStatus}
									</Badge>
								),
							},
							{
								id: "shape",
								header: "Objective · product",
								accessor: (r) => (
									<span className="text-xs text-muted">
										{r.objective} · {r.productType} · {r.placements.join(", ")}
									</span>
								),
							},
							{
								id: "bid",
								header: "Bid",
								accessor: (r) => <span className="font-mono text-xs">{bidText(r)}</span>,
							},
							{
								id: "flight",
								header: "Flight",
								accessor: (r) => <span className="font-mono text-xs">{flightText(r)}</span>,
							},
							{
								id: "open",
								header: "",
								accessor: (r) => {
									const hint = creativeHint(r.entityStatus);
									return (
										<>
											<Button variant="ghost" size="sm" onClick={() => setOpenLineItem(r)}>
												{OPEN_LINE_ITEM}
											</Button>
											{hint && <span className="ml-2 text-xs text-muted">{hint}</span>}
										</>
									);
								},
							},
							{
								id: "billed",
								header: SPEND_LABEL,
								accessor: (r) => {
									// One call, as on the campaigns screen, and for the reason that screen
									// states: the five states are told apart where a case can reach them.
									const state = billedRowState({
										id: r.id,
										asked: picked.ids,
										failed: billedFailed !== null,
										answer: billed,
									});
									return (
										<span
											className={
												state.kind === "figure" ? "text-sm text-content" : "text-xs text-muted"
											}
										>
											{billedCellText(state, currency)}
										</span>
									);
								},
							},
							{
								id: "urlClicks",
								header: FIGURE_LABELS.urlClicks,
								accessor: (r) => {
									// The same read and states as Spend; the card's wording for a count (twads#174).
									const state = metricRowState(
										{ id: r.id, asked: picked.ids, failed: billedFailed !== null, answer: billed },
										"urlClicks",
									);
									return (
										<span
											className={
												state.kind === "figure" ? "text-sm text-content" : "text-xs text-muted"
											}
										>
											{figureCellText(state, "urlClicks", currency)}
										</span>
									);
								},
							},
						]}
						data={rows}
						keyExtractor={(r) => r.id}
						failed={failed !== null}
						loading={loading}
						emptyMessage="X returned no line items for this campaign."
					/>

					{!loading && rows.length > 0 && (
						<div className="mt-3 flex flex-wrap items-center gap-3">
							<span className="text-sm text-muted">
								{loadedLabel(rows.length, cursor !== null)}
							</span>
							{cursor !== null && (
								<Button
									type="button"
									variant="outline"
									size="sm"
									loading={busy}
									onClick={() => void loadMore()}
								>
									Load more
								</Button>
							)}
						</div>
					)}

					{outcome && (
						<InfoBanner
							variant={outcome.kind === "created" ? "info" : "warning"}
							title={outcomeTitle(outcome)}
						>
							{outcome.kind === "exists"
								? EXISTS.body
								: outcome.kind === "unknown"
									? UNKNOWN.body
									: outcome.kind === "refused"
										? outcome.error
										: NO_TARGETING.body}
						</InfoBanner>
					)}
					{cannotDeliverYet(outcome) && (
						<InfoBanner variant="warning" title={NO_TARGETING.title}>
							{NO_TARGETING.body}
						</InfoBanner>
					)}

					{!writable && <p className="mt-4 text-sm text-muted">{READ_ONLY}</p>}

					{writable && (
						<form className="mt-6 grid max-w-xl gap-4" onSubmit={create}>
							<h3 className="text-sm font-semibold text-content">{CREATE_HEADING}</h3>
							<InfoBanner variant="info" title={PRICE_IS_X_TITLE}>
								{PRICE_IS_X}
							</InfoBanner>
							<p className="text-sm text-muted">{FIXED_SHAPE}</p>
							<p className="text-sm text-muted">{FLIGHT_IS_YOURS}</p>
							{formError && <InfoBanner variant="danger" title={formError} />}
							<Input
								label="Start (ISO 8601)"
								value={start}
								onChange={(e) => setStart(e.target.value)}
							/>
							<Input label="End (ISO 8601)" value={end} onChange={(e) => setEnd(e.target.value)} />
							<Select
								label="Objective"
								value={objective}
								onChange={(e) => setObjective(e.target.value as LineItemObjective)}
								options={OBJECTIVES.map((o) => ({ value: o.value, label: o.label }))}
							/>
							<p className="text-sm text-muted">{OBJECTIVE_NOTE}</p>
							{locked && (
								<InfoBanner variant="warning" title={CREATE_LOCKED}>
									{CREATE_LOCK_IS_LOCAL}
								</InfoBanner>
							)}
							<div className="flex flex-wrap items-center gap-3">
								<Button type="submit" loading={busy} disabled={locked}>
									Create line item
								</Button>
								{outcome?.kind === "exists" && outcome.id !== null && (
									<Button
										type="button"
										variant="outline"
										size="sm"
										loading={busy}
										onClick={() => void checkById()}
									>
										Check this line item in X
									</Button>
								)}
								{locked && (
									<Button
										type="button"
										variant="ghost"
										size="sm"
										disabled={busy}
										onClick={() => {
											// The lock is passed on purpose or not at all: the prompt names what it
											// may cost, and a dismissed prompt leaves the lock exactly as it was.
											if (!window.confirm(`${CREATE_ANYWAY.title}\n\n${CREATE_ANYWAY.body}`))
												return;
											setOutcome(null);
										}}
									>
										{CREATE_ANYWAY.label}
									</Button>
								)}
							</div>
						</form>
					)}
				</>
			)}
		</div>
	);
}
